import t from "@/lang/lang";
import { v4 } from "uuid";
import { useState } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { useOmniOfferCreateMutation } from "@/services/backend/omni/offer/api/offer-api";
import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { BranchSelectFrom } from "@/services/backend/company/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";
import { NullUUID } from "@/shared/lib/utilities/uuid";

/**
 * OfferSetupDropdownConfig configures the {@link OfferSetupDropdown}:
 * choices are its items, each creating an offer of its serviceKind with a
 * scope of work per service variation of scopeOfWorkServiceVariations.
 */
export type OfferSetupDropdownConfig = {
  choices: OfferSetupChoice[];
};

/** OfferSetupChoice is an item of the {@link OfferSetupDropdown}. */
export type OfferSetupChoice = {
  label: string;
  serviceKind: GSMServiceKind;
  scopeOfWorkServiceVariations: ServiceVariation[];
};

export function OfferSetupDropdown({
  processId,
  config,
  onCreated,
}: {
  processId: string;
  config: OfferSetupDropdownConfig;
  onCreated: (offerId: string) => void;
}) {
  const [choice, setChoice] = useState<OfferSetupChoice>();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [createOffer, { isLoading, reset }] = useOmniOfferCreateMutation();

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [selectedBranchId, setBranchId] = useState("");
  const [selectedServiceObjectId, setServiceObjectId] = useState<string>();
  const branchId = selectedBranchId || (branches[0] ?? "");
  // NullUUID is the choice "Kein Serviceobjekt".
  const serviceObjectId =
    selectedServiceObjectId ?? serviceObjects[0] ?? NullUUID;
  // Without a branch or service object to choose, the offer is created
  // right away.
  const needsDialog = branches.length !== 1 || serviceObjects.length > 1;

  const disabled =
    isLoading || !process || !process.customerId || branchId === "";

  const doSetup = (chosen: OfferSetupChoice) => {
    if (disabled) {
      return;
    }
    createOffer({
      id: v4(),
      serviceKind: chosen.serviceKind,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
      scopeOfWorkServiceVariations: chosen.scopeOfWorkServiceVariations,
    })
      .unwrap()
      .then((offer) => {
        setChoice(undefined);
        reset();
        onCreated(offer.id);
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
        reset();
      });
  };

  const onSelect = (chosen: OfferSetupChoice) => {
    if (needsDialog) {
      setChoice(chosen);
      return;
    }
    doSetup(chosen);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button disabled={disabled} size="sm">
            <Plus />
            {t("Angebot")}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {config.choices.map((s) => (
            <DropdownMenuItem key={s.label} onSelect={() => onSelect(s)}>
              {s.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={choice !== undefined}
        onOpenChange={(open) => !open && setChoice(undefined)}
      >
        <DialogContent>
          <CommonDialogHeader
            title={t("Angebot erstellen")}
            description={choice?.label}
          />
          {branches.length !== 1 && (
            <Field>
              <FieldLabel>{t("Niederlassung")}</FieldLabel>
              <BranchSelectFrom
                branchIds={branches}
                value={branchId}
                onValueChange={setBranchId}
              />
            </Field>
          )}
          {serviceObjects.length > 0 && (
            <Field>
              <FieldLabel>{t("Service Objekt")}</FieldLabel>
              <ServiceObjectSelectFrom
                serviceObjectIds={serviceObjects}
                customerId={process?.customerId}
                noneLabel={t("Kein Serviceobjekt")}
                value={serviceObjectId}
                onValueChange={setServiceObjectId}
              />
            </Field>
          )}
          <CommonDialogFooter
            isLoading={isLoading}
            disabled={disabled}
            actionLabel={t("Erstellen")}
            onClickAction={() => choice && doSetup(choice)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
